import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Compass } from "lucide-react";
import { motion } from "framer-motion";
import CategoryGrid from "../components/sections/CategoryGrid";
import TrendingSection from "../components/sections/TrendingSection";
import EventsSection from "../components/sections/EventsSection";
import AroundMeSection from "../components/sections/AroundMeSection";
import MapPreview from "../components/sections/MapPreview";

const HERO_IMG = "https://images.unsplash.com/photo-1683020369607-3ece7bf5f364?w=1800";

export default function Home() {
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/explore?q=${encodeURIComponent(q.trim())}`);
    else nav("/explore");
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Réunion lagon palmiers volcan" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-[#1A1A1A]/40 to-[#FFFBF5]" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#FF6B35]/15 via-transparent to-[#00A6B3]/15"/>
        </div>
        <div className="relative pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 sm:px-6 max-w-7xl mx-auto text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-1.5 uppercase-label backdrop-blur-md bg-white/15 border border-white/30 px-3 py-1.5 rounded-full text-white">
              <Compass size={12}/> Vibre péi
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4 max-w-2xl text-shadow-soft">
              Découvre <span className="text-[#FF6B35]">les meilleurs spots</span><br className="hidden sm:block"/> de l'île intense.
            </h1>
            <p className="text-base sm:text-lg mt-3 max-w-xl opacity-95 text-shadow-soft">
              Restos péi, plages turquoise, volcans, food trucks, rooftops & nightlife — tout sur une seule app.
            </p>
          </motion.div>

          <motion.form onSubmit={submitSearch}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 flex gap-2 sm:gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-[#1A1A1A]/50" />
              <input
                data-testid="hero-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cherche un resto, une plage, une soirée…"
                className="w-full pl-11 sm:pl-14 pr-4 h-14 rounded-full bg-white text-[#1A1A1A] placeholder:text-[#1A1A1A]/50 shadow-2xl shadow-black/20 focus:outline-none focus:ring-4 focus:ring-[#FF6B35]/30 text-sm sm:text-base font-medium"
              />
            </div>
            <button type="submit" data-testid="hero-search-btn"
              className="shrink-0 px-5 sm:px-7 h-14 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#E84A1F] text-white font-bold shadow-2xl shadow-[#FF6B35]/40 hover:scale-105 transition-transform">
              <span className="hidden sm:inline">Explorer</span>
              <Sparkles size={18} className="sm:hidden"/>
            </button>
          </motion.form>

          <div className="mt-6 flex flex-wrap gap-2 text-xs">
            {["plages", "rougail saucisses", "rooftops", "cascades", "festivals"].map((t, i) => (
              <button key={t} onClick={() => nav(`/explore?q=${encodeURIComponent(t)}`)}
                data-testid={`hero-suggestion-${i}`}
                className="backdrop-blur-md bg-white/15 hover:bg-white/25 border border-white/30 rounded-full px-3 py-1.5 font-medium transition-colors">
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Sections */}
      <div className="space-y-14 sm:space-y-20 py-12 sm:py-16">
        <CategoryGrid />
        <TrendingSection />
        <EventsSection />
        <AroundMeSection />
        <MapPreview />

        {/* Merchant CTA */}
        <section className="px-4 sm:px-6 max-w-7xl mx-auto" data-testid="merchant-cta">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#1A1A1A] to-[#2A1810] p-8 sm:p-12 text-white">
            <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[#FF6B35]/20 blur-3xl"/>
            <div className="absolute -left-10 -top-10 w-52 h-52 rounded-full bg-[#00A6B3]/30 blur-3xl"/>
            <div className="relative max-w-xl">
              <div className="uppercase-label text-[#FF6B35]">Espace Commerçant</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
                Vous êtes commerçant ? Boostez votre visibilité.
              </h2>
              <p className="text-white/75 mt-3">
                Ajoutez votre établissement et apparaissez en tête de liste avec un boost à partir de <span className="text-[#FF6B35] font-bold">9,99€</span>.
              </p>
              <a href="/merchant" data-testid="merchant-cta-btn" className="inline-block mt-6 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#E84A1F] hover:opacity-90 text-white font-bold py-3.5 px-7 shadow-xl shadow-[#FF6B35]/30 transition-opacity">
                Devenir commerçant →
              </a>
            </div>
          </div>
        </section>
      </div>

      <footer className="text-center py-8 text-xs text-[#1A1A1A]/40 font-medium">
        🌴 RunSpot · Île de la Réunion · 974 · Sortez, explorez, kiffez.
      </footer>
    </div>
  );
}
